/**
 * Feuille de brouillon — les bouts d'une ligne, côté affichage.
 *
 * Une ligne est une suite de bouts (`AtomItem`) et de fractions à deux cases
 * (`FracItem`, chaque case étant elle-même une petite ligne). Le moteur
 * (`@jeraisonne/reasoner`) ne lit que des atomes : `engineChips` traduit la
 * ligne en ce qu'il sait juger, `(haut) ÷ (bas)`.
 *
 * Portage fidèle du prototype validé (ui.js, v19). Pur : aucun DOM.
 */

import {
  atomsText,
  chipKey,
  chipKind,
  integralBoundsOf,
  isConst,
  isDiffAtom,
  isFunc,
  isNum,
  isSumAtom,
  isVar,
  sumBoundsOf,
  type Chip,
  type ChipKind,
} from '@jeraisonne/reasoner';

export interface AtomItem {
  frac?: false;
  atoms: string[];
  text: string;
  kind: ChipKind;
}
export interface FracItem {
  frac: true;
  num: LineItem[];
  den: LineItem[];
}
export type LineItem = AtomItem | FracItem;

export function isFrac(item: LineItem): item is FracItem {
  return item.frac === true;
}

/** Un bout de la palette ou d'une ligne recopiée, prêt à poser dans la ligne. */
export function atomItem(atoms: string[], text?: string): AtomItem {
  return { atoms: atoms.slice(), text: text ?? atomsText(atoms), kind: chipKind(atoms) };
}

/** Texte affiché : la barre « / » remplace « ÷ » (trop proche du « + » sur un petit écran). */
export function disp(t: string): string {
  return t.replace(/\s*÷\s*/g, '/');
}

/** exposant d'une matrice (moteur v30) : A³, A¹⁰, A⁻¹, Aᵀ */
const SUP_RE = /^[⁰¹²³⁴⁵⁶⁷⁸⁹⁻ᵀ]+$/;
/** nom de matrice d'une lettre (moteur v30) : A, X₁, π₀ */
const MAT_NAME_RE = /^([A-Z]|π)[₀-₉]*$/;

/** Un bout de plusieurs atomes qui s'affiche en écriture mathématique (fraction, matrice). */
export function needsMath(atoms: string[]): boolean {
  // intégrales (moteur v55) : « ∫(0 ; 1) » et « ](0 ; 1) » portent leurs bornes ;
  // sommes (moteur v59) : « Σ(k = 1 ; n) » aussi
  if (atoms.some((a) => integralBoundsOf(a) || isSumAtom(a))) return true;
  return (
    atoms.length > 1 &&
    (atoms.includes('÷') || atoms.includes('[') || atoms.some((a) => a !== '²' && SUP_RE.test(a)))
  );
}

/**
 * Ce que lit un lecteur d'écran pour un bout d'intégrale (moteur v55) : « intégrale de 0 à
 * 1 », « crochet fermé, de 0 à 1 » ; d'une somme (moteur v59) : « somme pour k de 1 à n » ;
 * le texte du bout sinon.
 */
export function spokenAtom(text: string): string {
  // somme, produit (moteur v59) : « somme pour k de 1 à n »
  const s = sumBoundsOf(text);
  if (s)
    return `${s.kind === 'sum' ? 'somme' : 'produit'} pour ${s.index} de ${disp(s.lo)} à ${disp(s.hi)}`;
  const b = integralBoundsOf(text);
  if (b)
    return b.kind === 'int'
      ? `intégrale de ${disp(b.lo)} à ${disp(b.hi)}`
      : `crochet fermé, de ${disp(b.lo)} à ${disp(b.hi)}`;
  if (text === '[') return 'crochet ouvert';
  // un reste de développement limité (moteur v58) : « o(x²) »
  const o = /^o\((.+)\)$/.exec(text);
  if (o) return `petit o de ${disp(o[1]!)}`;
  // un équivalent (moteur v61)
  if (text === '∼') return 'équivalent à';
  // une dérivée (moteur v62) : « y′ », « y″ », « y′(0) »
  const d = /^([A-Za-z])(′|″)(?:\((.+)\))?$/.exec(text);
  if (d) return `${d[1]} ${d[2] === '″' ? 'seconde' : 'prime'}${d[3] ? ` de ${disp(d[3])}` : ''}`;
  return disp(text);
}

/** Espace avant un atome, selon le précédent (même règle que l'écriture du moteur). */
export function gapBefore(prevAtoms: string[], first: string | undefined): string {
  const last = prevAtoms[prevAtoms.length - 1];
  const before = prevAtoms[prevAtoms.length - 2];
  // unité (moteur v4) : espace fine entre le nombre et l'unité (« 3,5 m³ »)
  if (first && first[0] === '\u2009') return '\u2009';
  if (first === '²' || first === ')' || first === '÷' || last === '÷') return '';
  // matrices (moteur v30) : « [1, 2 ; 3, 4] », A⁻¹B, det(A), 2A, π₀P²
  if (first === ']' || first === ',' || last === '[') return '';
  // intégrales (moteur v55) : le crochet fermé « ](0 ; 1) » se colle à ce qu'il ferme ;
  // « dx » se détache de la fonction (« exp(x) dx »)
  if (integralBoundsOf(first)?.kind === 'brk') return '';
  if (isDiffAtom(first)) return ' ';
  // un coefficient (moteur v52) : « (M²)₁₃ », les indices collés à la parenthèse
  if (last === ')' && first && /^[₀-₉]+$/.test(first)) return '';
  if (first && SUP_RE.test(first)) return '';
  if (last === 'det' && first === '(') return '';
  if (
    last &&
    (MAT_NAME_RE.test(last) || SUP_RE.test(last)) &&
    first &&
    (MAT_NAME_RE.test(first) || first === '(' || first === '[')
  )
    return '';
  // moteur v2 : 1,05^n, ln(5), 2π, 3cos(x)
  if (first === '^' || last === '^') return '';
  if (isFunc(last) && first === '(') return '';
  if (first === '%') return ' ';
  if (last === '(' || last === '√') return '';
  if (
    last === '−' &&
    (before === undefined ||
      before === '=' ||
      before === '→' ||
      before === '∼' ||
      before === '(' ||
      before === '[' ||
      before === ',' ||
      before === ';' ||
      before === 'ou' ||
      /^[+−×÷]$/.test(before))
  )
    return '';
  // signe « + » d'un nombre écrit entre parenthèses : « 6 − (+10) » (dans un groupe
  // entre parenthèses, l'affichage ne voit pas la parenthèse : `before` est absent)
  if (last === '+' && (before === '(' || before === undefined)) return '';
  if (
    (isNum(last) || last === ')' || last === ']' || last === '²') &&
    (isVar(first) || isConst(first) || isFunc(first) || first === '(' || first === '[')
  )
    return '';
  if (isVar(last) && isVar(first) && last!.length === 1 && first!.length === 1) return '';
  return ' ';
}

function wrapPart(a: string[]): string[] {
  return a.length === 1 ? a : ['(', ...a, ')'];
}

/** Les atomes d'une case de fraction (fractions imbriquées comprises). */
export function partAtoms(items: LineItem[]): string[] {
  return engineChips(items).flatMap((c) => c.atoms);
}

/** Ce que le moteur lit : (haut) ÷ (bas), avec des parenthèses si le voisinage l'exige. */
function fracAtoms(item: FracItem, prev: string | undefined, next: string | undefined): string[] {
  const core = [...wrapPart(partAtoms(item.num)), '÷', ...wrapPart(partAtoms(item.den))];
  const need =
    prev === '÷' || prev === '√' || next === '²' || next === '%' || SUP_RE.test(next ?? '');
  return need ? ['(', ...core, ')'] : core;
}

/** La ligne telle que le moteur la juge. */
export function engineChips(line: LineItem[]): Chip[] {
  const out: Chip[] = [];
  line.forEach((c, i) => {
    if (!isFrac(c)) {
      out.push({ atoms: c.atoms, text: c.text, kind: c.kind });
      return;
    }
    const prevC = out[out.length - 1];
    const prev = prevC ? prevC.atoms[prevC.atoms.length - 1] : undefined;
    const nextC = line[i + 1];
    const next = nextC ? (isFrac(nextC) ? '(' : nextC.atoms[0]) : undefined;
    out.push({ atoms: fracAtoms(c, prev, next), text: lineText([c]), kind: 'frac' });
  });
  return out;
}

/** Une case de fraction est-elle encore vide ? */
export function hasHole(line: LineItem[]): boolean {
  return line.some(
    (c) => isFrac(c) && (!c.num.length || !c.den.length || hasHole(c.num) || hasHole(c.den)),
  );
}

function partText(items: LineItem[]): string {
  const t = lineText(items);
  return partAtoms(items).length > 1 ? '(' + t + ')' : t;
}

/** Texte d'une ligne (aria, enregistrement en base). */
export function lineText(items: LineItem[]): string {
  let out = '';
  let atoms: string[] = [];
  items.forEach((c, i) => {
    const a = isFrac(c) ? ['(', ')'] : c.atoms;
    if (i > 0) out += gapBefore(atoms, a[0]);
    out += isFrac(c) ? partText(c.num) + '/' + partText(c.den) : disp(c.text);
    atoms = atoms.concat(a);
  });
  return out;
}

/** Les atomes d'une ligne, tels que le moteur les lit. */
export function lineAtomsOf(items: LineItem[]): string[] {
  return engineChips(items).flatMap((c) => c.atoms);
}

// ── termes repris des lignes validées : ils deviennent des bouts ──────────────

/**
 * Termes composés d'une ligne (BC², 6², 2ⁿ − 1…), coupés au niveau 0 sur = ou + − × ÷,
 * sur une comparaison (< > ≤ ≥ ≠ ≈ ≡), sur « → » (limite, moteur v54) et sur « ; » —
 * « x³ − x > 0 » donne « x³ », jamais
 * « x > 0 » ; une matrice (moteur v30) n'est jamais coupée.
 */
export function termsOf(atoms: string[]): string[][] {
  const out: string[][] = [];
  let cur: string[] = [];
  let depth = 0;
  // somme (moteur v59) : « Σ(k = 1 ; n) 1 ÷ (n + k) » reste entière — le terme général va
  // jusqu'au « + » ou « − » suivant (pas son signe), au « = » ou à la flèche
  let inSum = false;
  [...atoms, '='].forEach((a) => {
    // intégrales (moteur v55) : « ∫(0 ; 1) … dx » et « [ … ](0 ; 1) » restent entiers
    const lim = integralBoundsOf(a);
    if (a === '(' || a === '[' || lim?.kind === 'int') depth++;
    if (a === ')' || a === ']' || lim?.kind === 'brk' || isDiffAtom(a)) depth--;
    if (depth === 0 && isSumAtom(a)) inSum = true;
    const sumGlue =
      inSum &&
      (a === '×' || a === '÷' || ((a === '+' || a === '−') && isSumAtom(cur[cur.length - 1])));
    if (depth === 0 && !sumGlue && /^(=|ou|\+|−|×|÷|<|>|≤|≥|≠|≈|≡|;|→)$/.test(a)) {
      if (cur.length >= 2) out.push(cur);
      cur = [];
      inSum = false;
    } else {
      cur.push(a);
    }
  });
  return out;
}

/** (2ⁿ − 1) et 2ⁿ − 1 sont le même bout. */
export function stripParens(t: string[]): string[] {
  let r = t;
  while (r.length > 2 && r[0] === '(' && r[r.length - 1] === ')') {
    let depth = 0;
    let outer = true;
    for (let i = 0; i < r.length - 1; i++) {
      if (r[i] === '(') depth++;
      if (r[i] === ')') depth--;
      if (depth === 0) {
        outer = false;
        break;
      }
    }
    if (!outer) break;
    r = r.slice(1, -1);
  }
  return r;
}

/**
 * une intégrale « ∫(0 ; 1) … dx » ou un crochet « [ … ](0 ; 1) » entier (moteur v55), une
 * somme « Σ(k = 1 ; n) … » entière (moteur v59)
 */
function integralTerm(t: string[]): boolean {
  return (
    (integralBoundsOf(t[0])?.kind === 'int' && isDiffAtom(t[t.length - 1])) ||
    (t[0] === '[' && integralBoundsOf(t[t.length - 1])?.kind === 'brk') ||
    isSumAtom(t[0])
  );
}

/**
 * Les termes nouveaux des lignes validées (2 à 6 atomes ; une intégrale ou un crochet
 * entier jusqu'à 16), hors bouts déjà proposés ;
 * `text` écrit un terme (matrices, moteur v30 : `matText`).
 */
export function learnedItems(
  lines: LineItem[][],
  taken: Set<string>,
  text?: (atoms: string[]) => string,
): AtomItem[] {
  const seen = new Set(taken);
  const out: AtomItem[] = [];
  lines.forEach((l) => {
    termsOf(lineAtomsOf(l)).forEach((raw) => {
      const t = stripParens(raw);
      if (t.length < 2 || t.length > (integralTerm(t) ? 16 : 6)) return;
      const k = chipKey(t);
      if (seen.has(k)) return;
      seen.add(k);
      out.push(atomItem(t, text?.(t)));
    });
  });
  return out;
}
